import { batch, bind, Component, createRef, h, signal, tag } from "omi";
import classNames from "classnames";
import isEmpty from "lodash-es/isEmpty";
import { v4 as uuid } from "uuid";

import { IconName } from "@/components/atoms/nw-icon/icons";
import { NWAvatar } from "@/components/base/nw-avatar";
import { NWBadge, NWButton, NWIcon, NWTooltip, TooltipProps } from "@/components/atoms";
import { AuthService } from "@/services/authService";
import { addToClipboard } from "@/utils/clipboard";
import { networkSignal } from "@/utils/signals";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { initApi } from "@/utils";

type Props = {};

type MenuItem = {
  type: "button" | "link" | "divider";
  icon?: IconName;
  text?: string;
  tooltipOptions?: TooltipProps;
  color?: "red" | "gray";
  comingSoon?: boolean;
  callback?: () => void;
  href?: string;
};

@tag("toolbar-views-user")
export class NWToolbarViewUser extends Component<Props> {
  static css = [tailwind];
  private authService: AuthService | undefined;
  private isEditingName = signal<boolean>(false);
  private nameInput = createRef<HTMLInputElement>();
  private fileInput = createRef<HTMLInputElement>();
  private newName = signal<string>("");
  private usernameUpdate = networkSignal({ retry: { delayMs: 500, backoffMultiplier: 2, maxAttempts: 3 } });
  private showEditButton = signal<boolean>(true);
  private avatarUpload = networkSignal({ retry: { delayMs: 500, backoffMultiplier: 2, maxAttempts: 3 } });
  private specificError = signal<string | undefined>(undefined);

  installed() {
    this.authService = new AuthService();
    this.newName.value = Store.user.info.value?.name || "";
  }

  private createMenuItems(): MenuItem[] {
    const items: MenuItem[] = [
      {
        type: "button",
        icon: "heart-hand",
        text: "Invite a friend",
        tooltipOptions: {
          content: "Copied! Send to a friend 🚀",
          trigger: "click",
          placement: "top",
          strategy: "absolute",
        },
        callback: this.handleInviteCopy,
      },
      { type: "divider" },
      {
        type: "link",
        icon: "file",
        text: "Terms & Conditions",
        href: "https://www.nextwork.org/legal/terms-conditions",
      },
      {
        type: "link",
        icon: "key",
        text: "Privacy policy",
        href: "https://www.nextwork.org/legal/privacy-policy",
      },
      { type: "divider" },
      {
        type: "button",
        icon: "logout",
        text: "Log out",
        callback: this.handleLogout,
      },
    ];

    if (Store.features.isAdmin) {
      items.push(
        {
          type: "divider",
        },
        {
          type: "button",
          text: "Admin settings",
          icon: "admin",
          callback: () => Store.app.showToolbarView("admin"),
          color: "red",
        },
      );
    }
    return items;
  }

  @bind
  private async handleInviteCopy() {
    const inviteLink = "https://link.nextwork.org/app?utm_source=app&utm_medium=nav&utm_campaign=referral";
    await addToClipboard(inviteLink);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackInviteLinkCopied("user-menu");
    });
  }

  @bind
  private handleLogout() {
    this.authService?.logout();
  }

  @bind
  private handleItemClick(item: MenuItem) {
    if (item.type === "button" && item.callback) {
      item.callback();
    }
  }

  @bind
  private toggleEditingName() {
    this.isEditingName.value = !this.isEditingName.value;
    if (this.isEditingName.value) {
      this.newName.value = Store.user.info.value?.name || "";
      setTimeout(() => {
        this.nameInput.current?.focus();
      }, 0);
    }
  }

  @bind
  private handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.newName.value = input.value;
  }

  @bind
  private handleInputBlur(e: FocusEvent) {
    if (this.usernameUpdate.signal.isLoading.value) {
      e.preventDefault();
      return;
    }

    this.isEditingName.value = false;
  }

  @bind
  private handleSaveClick() {
    if (this.newName.value.trim() && this.newName.value !== Store.user.info.value?.name) {
      const api = initApi();
      const name =
        this.newName.value.trim().length > 0
          ? this.newName.value.trim()
          : (Store.user.info.value?.name ?? "NextWork Student");

      this.showEditButton.value = false;
      this.usernameUpdate
        .run(async () => {
          const userInfo = await api.setUserName({ nameUpdate: { name } });
          Store.user.setUserInfo(userInfo);
          this.isEditingName.value = false;
        })
        .then(() => {
          setTimeout(() => {
            this.showEditButton.value = true;
          }, 400);
        });

      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackUserProfileUpdated("username", "user-menu");
      });
    } else {
      batch(() => {
        this.newName.value = Store.user.info.value?.name || "";
        this.isEditingName.value = false;
        this.showEditButton.value = false;
      });

      setTimeout(() => {
        this.showEditButton.value = true;
      }, 400);
    }
  }

  @bind
  private handleInputKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      this.handleSaveClick();
    }
  }

  @bind
  private async handleGetUploadUrl(fileId: string): Promise<string | undefined> {
    if (!Store.user.isLoggedIn.value) {
      return undefined;
    }

    const response = await fetch(`/uploads/generate-signed-url?fileName=${fileId}`);

    if (!response.ok) {
      throw new Error("Unable to create signed url");
    }

    const resJson = await response.json();
    return resJson.url;
  }

  @bind
  private async handleOnAvatarUploadSuccess(fileId: string) {
    const api = initApi();
    const userInfo = await api.setUserPicture({
      pictureUpdate: { picture: `/${Store.user.getNickname()}/uploads/${fileId}` },
    });
    Store.user.setUserInfo(userInfo);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackUserProfileUpdated("avatar", "user-menu");
    });
  }

  @bind
  private openFileSelection() {
    if (this.specificError.value) {
      this.specificError.value = undefined;
    }
    this.fileInput.current?.click();
  }

  @bind
  private async handleAvatarUpload(event: Event) {
    await this.avatarUpload.run(async () => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.specificError.value = "Please upload a JPEG, or PNG file.";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.specificError.value = "File is too large. Maximum size is 5MB. Please try again.";
        return;
      }

      const fileId = `profile_picture_${uuid()}`;
      const uploadUrl = await this.handleGetUploadUrl(fileId);

      if (!uploadUrl) {
        throw new Error("Unable to get signed URL for upload. Please try again later.");
      }

      await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "application/octet-stream",
        },
      });

      await this.handleOnAvatarUploadSuccess(fileId);
    });
  }

  renderMenuItems() {
    const menuItems = this.createMenuItems();
    const itemStyles =
      "text-secondary cursor-pointer select-none text-sm px-4 flex-1 py-5 flex items-center transition-colors hover:bg-primary space-x-2 w-full";

    return (
      <ul class="flex py-1 items-center self-stretch flex-col">
        {menuItems.map(item => {
          if (item.type === "divider") return <li class="border-b bg-gray-200 w-full my-0.5" />;

          const content = (
            <>
              {item.icon && (
                <div class="text-quaternary">
                  <NWIcon name={item.icon} width={16} height={16} />
                </div>
              )}
              <span class="text-nowrap whitespace-nowrap flex-grow">{item.text}</span>
            </>
          );

          if (item.type === "link" && item.href) {
            return (
              <a class={itemStyles} tabIndex={0} href={item.href} target="_blank">
                {content}
              </a>
            );
          }

          const listItem = (
            <li
              onClick={() => this.handleItemClick(item)}
              tabIndex={0}
              class={classNames(itemStyles, { "bg-error-100": item.color === "red" })}
            >
              {content}
              {item.comingSoon && (
                <NWBadge color="gray-filled" padding="xs">
                  <span class="text-secondary normal-case">Coming soon</span>
                </NWBadge>
              )}
            </li>
          );

          if (item.tooltipOptions) {
            return (
              <NWTooltip {...item.tooltipOptions} class="w-full">
                {listItem}
              </NWTooltip>
            );
          }

          return listItem;
        })}
      </ul>
    );
  }

  render() {
    const user = Store.user.info.value;
    const hasError =
      this.avatarUpload.signal.error.value || this.usernameUpdate.signal.error.value || this.specificError.value;
    return (
      <>
        <div class="bg-white border border-gray-200 rounded-3xl relative w-[30rem]">
          <div className="flex flex-col py-6 px-4 gap-4 max-h-[80svh] overflow-y-auto">
            <div class="px-4 relative">
              {hasError && (
                <p class="mb-4 animate-fadeInFast text-xs text-error-500 bg-error-50 p-1 rounded-md text-center border border-error-600 break-words max-w-64 mx-auto">
                  {this.specificError.value ?? "Something went wrong! Please try again."}
                </p>
              )}
              {user?.name ? (
                <div class="">
                  <div class="mb-2">
                    <div class="mb-3 flex items-center gap-1.5">
                      <div class="flex flex-col items-center -space-y-3">
                        <button class="group relative" onClick={this.openFileSelection}>
                          <NWAvatar
                            className={classNames("group-hover:brightness-75 group-hover:transition-[filter]", {
                              "animate-pulse brightness-75": this.avatarUpload.signal.isLoading.value,
                            })}
                            withProBorder={true}
                            size="lg"
                            shape="circle"
                            withIndicator={false}
                            interactive={true}
                          />
                        </button>
                        {Store.user.isPro() && (
                          <NWBadge
                            className="z-10"
                            color="brand"
                            padding="xs"
                            iconSize={{ width: 7, height: 12 }}
                            iconOnly={false}
                          >
                            PRO
                          </NWBadge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col items-start flex-grow-0">
                    <div class="flex items-center group gap-1 max-w-[256px] h-8">
                      {this.isEditingName.value ? (
                        <input
                          className="text-primary text-xl font-semibold w-full border-b border-gray-300 bg-transparent outline-none focus-visible:border-brand-400"
                          type="text"
                          value={this.newName.value}
                          ref={this.nameInput}
                          onBlur={this.handleInputBlur}
                          onInput={this.handleInputChange}
                          onKeyDown={this.handleInputKeyDown}
                        />
                      ) : (
                        <p className="text-primary mb-[1px] text-xl font-semibold truncate w-full" title={user.name}>
                          {user.name}
                        </p>
                      )}
                      {this.isEditingName.value ? (
                        <NWButton
                          type="text"
                          size="fit"
                          iconSize="sm"
                          class="flex-shrink-0 text-quaternary"
                          onClick={this.handleSaveClick}
                          onMouseDown={e => e.preventDefault()}
                          loading={this.usernameUpdate.signal.isLoading.value}
                        >
                          Save
                        </NWButton>
                      ) : (
                        <button class="p-0.5 flex-shrink-0" onClick={this.toggleEditingName}>
                          <NWTooltip content="Edit username" placement="top" delay={[300, 0]} strategy="absolute">
                            <NWIcon
                              class={classNames("text-quaternary transition-opacity hover:text-tertiary-hover", {
                                "opacity-0 group-hover:opacity-100": this.showEditButton.value,
                                "opacity-0": !this.showEditButton.value,
                              })}
                              name="pencil"
                              width={16}
                              height={16}
                            />
                          </NWTooltip>
                        </button>
                      )}
                    </div>
                  </div>
                  {!isEmpty(user?.email) && (
                    <p className="text-tertiary text-lg truncate max-w-64" title={user.email}>
                      {user.email}
                    </p>
                  )}
                </div>
              ) : (
                <div class="flex items-center justify-between">
                  <p className="text-secondary text-sm font-medium truncate mr-2">
                    {user?.email || "NextWork Student"}
                  </p>
                  {Store.user.isPro() && (
                    <NWBadge
                      color="brand"
                      padding="xs"
                      iconSize={{ width: 7, height: 12 }}
                      iconOnly={false}
                      icon="keyhole"
                    >
                      PRO
                    </NWBadge>
                  )}
                </div>
              )}
            </div>
            {this.renderMenuItems()}
          </div>
        </div>
        <input
          type="file"
          ref={this.fileInput}
          multiple={false}
          className="hidden"
          accept="image/png,image/jpeg"
          onChange={this.handleAvatarUpload}
        />
      </>
    );
  }
}
